import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

interface PartnersProps {
  t: any;
  isRTL: boolean;
}

const PartnersSection = styled.section`
  padding: 6rem 0;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse"><path d="M 20 0 L 0 0 0 20" fill="none" stroke="%23dc2626" stroke-width="0.5" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
    opacity: 0.3;
  }
`;

const Container = styled.div<{ $isRTL: boolean }>`
  max-width: 1400px;
  margin: 0 auto;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  position: relative;
  z-index: 1;
  padding: 0 2rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const SectionTitle = styled(motion.h2)<{ $isRTL: boolean }>`
  font-size: 3.5rem;
  color: #1f2937;
  margin-bottom: 1.5rem;
  position: relative;
  font-weight: 700;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  background: linear-gradient(135deg, #0f172a 0%, #dc2626 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -20px;
    left: 50%;
    transform: translateX(-50%);
    width: 120px;
    height: 6px;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    border-radius: 3px;
    box-shadow: 0 4px 12px rgba(220, 38, 38, 0.4);
  }
  
  @media (max-width: 768px) {
    font-size: 2.8rem;
  }
`;

const SectionSubtitle = styled(motion.p)<{ $isRTL: boolean }>`
  font-size: 1.3rem;
  color: #6b7280;
  margin-top: 2.5rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const SliderContainer = styled.div`
  position: relative;
  overflow: hidden;
  margin: 3rem 0;
  padding: 2rem 0;
  width: 100vw;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
`;

const SliderTrack = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 4rem;
  will-change: transform;
  padding: 0 2rem;
  width: fit-content;
`;

const PartnerLogo = styled.div<{ 
  $isIfamel?: boolean; 
  $isDeutz?: boolean; 
  $isCahors?: boolean; 
  $isInes?: boolean; 
  $isMarcont?: boolean;
  $isAbb?: boolean;
  $isLegrand?: boolean;
}>`
  width: 200px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(220, 38, 38, 0.1);
  transition: all 0.3s ease;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-8px) scale(1.05);
    box-shadow: 0 16px 48px rgba(0, 0, 0, 0.15);
    border-color: rgba(220, 38, 38, 0.3);
    
    &::before {
      transform: scaleX(1);
    }
  }
  
  img {
    max-width: ${props => {
      if (props.$isDeutz) return '130%';
      if (props.$isCahors) return '180%';
      if (props.$isIfamel) return '300%';
      if (props.$isInes) return '160%';
      if (props.$isMarcont) return '200%';
      if (props.$isAbb) return '120%';
      if (props.$isLegrand) return '140%';
      return '100%';
    }};
    max-height: ${props => {
      if (props.$isDeutz) return '130%';
      if (props.$isCahors) return '180%';
      if (props.$isIfamel) return '300%';
      if (props.$isInes) return '160%';
      if (props.$isMarcont) return '200%';
      if (props.$isAbb) return '120%';
      if (props.$isLegrand) return '140%';
      return '100%';
    }};
    object-fit: contain;
    transition: all 0.3s ease;
    
    ${props => props.$isIfamel && `
      /* Remove white background from IFAMEL logo */
      background: transparent;
      mix-blend-mode: multiply;
      filter: contrast(1.2) brightness(1.1);
    `}
  }
  
  &:hover img {
    transform: scale(1.1);
    
    ${props => props.$isIfamel && `
      /* Enhanced effect on hover for IFAMEL */
      filter: contrast(1.3) brightness(1.2);
    `}
  }
  
  @media (max-width: 768px) {
    width: 160px;
    height: 100px;
    padding: 1.5rem;
    
    img {
      max-width: ${props => {
        if (props.$isDeutz) return '140%';
        if (props.$isCahors) return '190%';
        if (props.$isIfamel) return '320%';
        if (props.$isInes) return '170%';
        if (props.$isMarcont) return '210%';
        if (props.$isAbb) return '130%';
        if (props.$isLegrand) return '150%';
        return '100%';
      }};
      max-height: ${props => {
        if (props.$isDeutz) return '140%';
        if (props.$isCahors) return '190%';
        if (props.$isIfamel) return '320%';
        if (props.$isInes) return '170%';
        if (props.$isMarcont) return '210%';
        if (props.$isAbb) return '130%';
        if (props.$isLegrand) return '150%';
        return '100%';
      }};
    }
  }
`;

const Partners: React.FC<PartnersProps> = ({ t, isRTL }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  // Define partner data
  const partners = [
    {
      name: t.partnerDetails?.nexans?.name || 'Nexans',
      logo: '/images/partners/nexans.png'
    },
    {
      name: t.partnerDetails?.deutz?.name || 'DEUTZ',
      logo: '/images/partners/deutz.png'
    },
    {
      name: t.partnerDetails?.ines?.name || 'Ines Max',
      logo: '/images/partners/ines.png'
    },
    {
      name: t.partnerDetails?.cahors?.name || 'CAHORS',
      logo: '/images/partners/cahors.png'
    },
    {
      name: t.partnerDetails?.ifamel?.name || 'IFAMEL',
      logo: '/images/partners/ifamel.png'
    },
    {
      name: t.partnerDetails?.marcont?.name || 'Marcont',
      logo: '/images/partners/marcont.png'
    },
    {
      name: t.partnerDetails?.abb?.name || 'ABB',
      logo: '/images/partners/ABB.svg'
    },
    {
      name: t.partnerDetails?.legrand?.name || 'Legrand',
      logo: '/images/partners/legrand.jpg'
    }
  ];

  // Create multiple sets of partners for continuous sliding
  const duplicatedPartners = [...partners, ...partners, ...partners, ...partners];
  
  // Calculate the width of one set of partners (8 partners * 200px + 7 gaps * 4rem = 64px)
  const singleSetWidth = (partners.length * 200) + ((partners.length - 1) * 64) + 64; // 8*200 + 7*64 + 64 = 2000px

  return (
    <PartnersSection id="partners" ref={ref}>
      <Container $isRTL={isRTL}>
        <SectionHeader>
          <SectionTitle
            $isRTL={isRTL}
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            {t.partnersTitle}
          </SectionTitle>
          <SectionSubtitle
            $isRTL={isRTL}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
{t.partnersSubtitle}
          </SectionSubtitle>
        </SectionHeader>
      </Container>

      <SliderContainer>
        <SliderTrack
          animate={{
            x: isRTL ? [0, singleSetWidth] : [0, -singleSetWidth]
          }}
          transition={{
            duration: 25, // 25 seconds for one complete cycle
            repeat: Infinity,
            ease: "linear",
            repeatType: "loop"
          }}
        >
          {duplicatedPartners.map((partner, index) => (
            <motion.div
              key={`${partner.name}-${index}`}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ 
                duration: 0.6, 
                delay: index * 0.1,
                type: "spring",
                stiffness: 100
              }}
              whileHover={{ 
                scale: 1.05,
                transition: { duration: 0.2 }
              }}
            >
              <PartnerLogo 
                $isIfamel={partner.name.toLowerCase().includes('ifamel')}
                $isDeutz={partner.name.toLowerCase().includes('deutz')}
                $isCahors={partner.name.toLowerCase().includes('cahors')}
                $isInes={partner.name.toLowerCase().includes('ines')}
                $isMarcont={partner.name.toLowerCase().includes('marcont')}
                $isAbb={partner.name.toLowerCase().includes('abb')}
                $isLegrand={partner.name.toLowerCase().includes('legrand')}
              >
                <img src={partner.logo} alt={partner.name} />
              </PartnerLogo>
            </motion.div>
          ))}
        </SliderTrack>
      </SliderContainer>
    </PartnersSection>
  );
};

export default Partners;
